<template>
  <!-- ARIA live region for announcements (only show for filtered listings, not slug-based) -->
  <div
    v-if="!isSlugMode"
    aria-live="polite"
    aria-atomic="true"
    class="visually-hidden"
    :aria-label="ariaAnnouncement"
  >
    {{ ariaAnnouncement }}
  </div>

  <div v-if="loading" class="movie-listing">
    <div v-for="n in ghostCount" :key="n" class="container-standard">
      <MovieBlockGhost />
    </div>
  </div>
  <div v-else-if="hasMovies" class="movie-listing">
    <div v-for="movie in movies" :key="movie.key || movie.id" class="container-standard">
      <MovieBlock :movie="movie" />
    </div>
  </div>
  <div v-else class="mb-el d-flex flex-column align-items-center text-center gap-3">
    <div class="h3">{{ emptyStateTitle }}</div>
    <p v-if="emptyStateMessage && !emptyStateMessage.length">{{ emptyStateMessage }}</p>
    <button
      v-if="showResetButton"
      type="button"
      class="btn btn-outline"
      @click="resetFilters"
      aria-label="Alle Filter zurücksetzen und alle Filme anzeigen"
    >
      <span>Filter zurücksetzen</span>
      <ui-icon icon="close-20"></ui-icon>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import MovieBlock from "./components/MovieBlock.vue";
import MovieBlockGhost from "./components/MovieBlockGhost.vue";
import { useUrlHelpers } from "./composables/useUrlHelpers.js";
import { useScheduleStore } from "./stores/scheduleStore.js";

const { buildQueryString } = useUrlHelpers();
const scheduleStore = useScheduleStore();

const ghostCount = ref(3); // Number of ghost components to show while loading
const ariaAnnouncement = ref(""); // For screen reader announcements

// Debounce timer for API calls
let debounceTimer = null;
const DEBOUNCE_DELAY = 300; // Wait 300ms after last filter change before making API call

// Check if we're fetching by slugs (from data-slugs attribute)
const slugs = ref(null);
const isSlugMode = computed(() => slugs.value !== null && slugs.value.length > 0);

// Check if FilterBar is rendered
const isFilterBarRendered = ref(false);

// Track if initial config (including scheduleView) has been loaded
const initialConfigLoaded = ref(false);

// Use store state (Pinia stores are reactive, so we can use them directly)
const movies = computed(() => scheduleStore.films);
const loading = computed(() => scheduleStore.loading);

// Computed properties for better performance
const hasMovies = computed(() => movies.value.length > 0);

// Empty state messages
const emptyStateTitle = computed(() => {
  if (slugs.value !== null) {
    return slugs.value.length === 0 ? "Keine Filme verfügbar." : "Fehler beim Laden der Filme.";
  }
  return "Keine Filme gefunden.";
});

const emptyStateMessage = computed(() => {
  if (slugs.value !== null) {
    return slugs.value.length === 0
      ? "Derzeit sind keine Filme verfügbar."
      : "Die angeforderte Filmauswahl konnte nicht geladen werden.";
  }
  return isFilterBarRendered.value
    ? "Es wurden keine Filme gefunden die den aktuellen Filterkriterien entsprechen."
    : "";
});

const showResetButton = computed(() => {
  return slugs.value === null && isFilterBarRendered.value;
});

// Get current filter state from store
const getCurrentFilters = () => {
  const filterParams = {};
  const filterKeys = ["series", "date", "release", "cinema", "auditorium", "fsk"];

  filterKeys.forEach((filterKey) => {
    const activeFilters = scheduleStore.getActiveFilterValues(filterKey);
    if (activeFilters.length > 0) {
      filterParams[filterKey] = activeFilters;
    }
  });

  return filterParams;
};

// Handle filter changes from FilterBar (with debouncing)
const onFilterChange = async (e) => {
  try {
    if (e.detail) {
      // Clear any pending debounce timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Set loading state immediately for better UX
      scheduleStore.setLoading(true);
      ariaAnnouncement.value = "Filme werden geladen...";

      // Debounce the actual API call
      debounceTimer = setTimeout(async () => {
        await loadFilteredFilms();
        debounceTimer = null;
      }, DEBOUNCE_DELAY);
    }
  } catch (error) {
    console.error("Error handling filter change:", error);
    scheduleStore.setLoading(false);
  }
};

// Load filtered films using fetch API
// Always uses global filters from Pinia store
const loadFilteredFilms = async () => {
  try {
    let url = "/programm/api/filtered-films";

    // Build query params - combine slugs and filters if both are present
    const queryParams = {};

    // Add slugs if in slug mode
    if (isSlugMode.value && slugs.value.length > 0) {
      queryParams.slugs = slugs.value.join(",");
    }

    // Get current filters from global store and add them
    const filters = getCurrentFilters();
    Object.assign(queryParams, filters);

    const queryString = buildQueryString(queryParams);
    url = `${url}?${queryString}`;

    // testing:
    // console.log("[API] Fetching films:", url, { filters, queryParams });

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    // console.log("filteredfilms: ", data);
    window.data_testing = data;

    // Update store with data from API response
    if (data.config || data.filters || data.scheduleView) {
      // Only apply scheduleView from API on initial load (not on subsequent filter changes)
      const shouldApplyScheduleView = !initialConfigLoaded.value;
      const scheduleView = isSlugMode.value ? "table" : data.scheduleView;

      scheduleStore.setConfig({
        config: data.config,
        filters: data.filters,
        scheduleView: shouldApplyScheduleView ? scheduleView : undefined,
      });

      // Mark initial config as loaded
      if (!initialConfigLoaded.value) {
        initialConfigLoaded.value = true;
      }
    }

    // Update films in store
    const filmCount = data.films?.length || 0;
    scheduleStore.setFilms(data.films || []);

    // Announce results to screen readers
    if (filmCount > 0) {
      ariaAnnouncement.value = `${filmCount} ${filmCount === 1 ? "Film gefunden" : "Filme gefunden"}.`;
    } else {
      ariaAnnouncement.value = "Keine Filme gefunden. Bitte passen Sie Ihre Filter an.";
    }

    // Dispatch event for other components that might need the data
    const event = new CustomEvent("vue-movie-listing-filters-applied");
    window.dispatchEvent(event);
  } catch (error) {
    console.error("Failed to load filtered films:", error);
    scheduleStore.setFilms([]);
    ariaAnnouncement.value = "Fehler beim Laden der Filme. Bitte versuchen Sie es erneut.";
  } finally {
    scheduleStore.setLoading(false);
  }
};

// Reset all filters
const resetFilters = () => {
  // Dispatch event to reset filters in FilterBar
  // Note: This will reset filters even when in slug mode, allowing users to see all films from the slugs
  window.dispatchEvent(
    new CustomEvent("vue-filterbar-reset", {
      bubbles: true,
    }),
  );
};

onMounted(async () => {
  window.addEventListener("vue-filterbar-values-changed", onFilterChange);

  // Wait for next tick to ensure DOM is fully rendered and component is mounted
  await nextTick();

  // Check if FilterBar is rendered (check if #filterbar exists and contains the filterbar component)
  const filterBarEl = document.querySelector("#filterbar");
  if (filterBarEl && filterBarEl.querySelector(".filterbar")) {
    isFilterBarRendered.value = true;
  }

  // Get the component's mount point element (the #movielisting element)
  // Use getElementById for reliable access to the mount point
  const listingEl = document.getElementById("movielisting");

  if (listingEl) {
    // Get cityId from data attribute on the container element and store it in the global state
    const cityIdAttr = listingEl.getAttribute("data-city-id");
    if (cityIdAttr) {
      scheduleStore.setCityId(cityIdAttr);
    }

    // Check for data-initial-filters attribute to set initial filter state
    // This must happen BEFORE slugs check so filters are set globally
    const initialFiltersAttr = listingEl.getAttribute("data-initial-filters");
    if (initialFiltersAttr) {
      try {
        const initialFilters = JSON.parse(initialFiltersAttr);
        if (initialFilters && typeof initialFilters === "object") {
          // Set filters globally in Pinia store
          Object.keys(initialFilters).forEach((filterKey) => {
            if (scheduleStore.activeFilters[filterKey] !== undefined) {
              scheduleStore.setActiveFilter(filterKey, initialFilters[filterKey]);
            }
          });
        }
      } catch (error) {
        console.error("Failed to parse data-initial-filters:", error);
      }
    }

    // Check for data-slugs attribute to determine if we're in slug mode
    const slugsAttr = listingEl.getAttribute("data-slugs");
    if (slugsAttr) {
      slugs.value = slugsAttr
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      ghostCount.value = slugs.value.length || 3;

      // Set schedule view to table for slug-based displays
      scheduleStore.setScheduleView("table");

      // If in slug mode, load immediately (filters can be combined with slugs)
      scheduleStore.setLoading(true);
      await loadFilteredFilms();
      return;
    }

    // If initial filters were set, load films immediately
    if (initialFiltersAttr) {
      scheduleStore.setLoading(true);
      await loadFilteredFilms();
      return;
    }
  }

  // Don't load on mount - wait for FilterBar to initialize from URL first
  // FilterBar will dispatch 'vue-filterbar-values-changed' event after initialization
  // This prevents duplicate API calls on page load
});

onBeforeUnmount(() => {
  window.removeEventListener("vue-filterbar-values-changed", onFilterChange);

  // Clear any pending debounce timer and reset loading state
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
    scheduleStore.setLoading(false);
  }
});
</script>
